import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import GroupsIcon from "@mui/icons-material/Groups";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import VideocamIcon from "@mui/icons-material/Videocam";
import CallIcon from "@mui/icons-material/Call";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { FixedSizeList } from "react-window";
import { application } from "globals/endpoints";
import loggerFactory from "globals/logger/logger-factory";

const isSameParticipantList = (previousList = [], nextList = []) => {
  if (previousList.length !== nextList.length) return false;
  for (let index = 0; index < previousList.length; index += 1) {
    const previous = previousList[index];
    const next = nextList[index];
    if (
      previous?.id !== next?.id ||
      previous?.uuid !== next?.uuid ||
      previous?.contact !== next?.contact ||
      previous?.canSpeak !== next?.canSpeak ||
      previous?.hasVideo !== next?.hasVideo ||
      previous?.talking !== next?.talking ||
      previous?.isHandRaised !== next?.isHandRaised
    ) {
      return false;
    }
  }
  return true;
};

const getAccessCodeFromEvent = (payload) => {
  return (
    payload?.data?.conferenceRoomNumber ||
    payload?.conferenceRoomNumber ||
    payload?.data?.accessCode ||
    payload?.accessCode ||
    ""
  );
};

export default function ActiveConferenceParticipants() {
  const columnTemplate = "1.5fr 1fr 1fr 0.8fr 1fr";
  const rowHeight = 24;
  const listContainerRef = useRef(null);
  const [accessCode, setAccessCode] = useState("");
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notify, setNotify] = useState({
    open: false,
    type: "success",
    message: "",
  });
  const [listHeight, setListHeight] = useState(260);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  const conferenceSuffix = useMemo(() => {
    if (!accessCode) return "-";
    const splitCode = accessCode.split("_");
    return splitCode[splitCode.length - 1] || "-";
  }, [accessCode]);

  const showNotification = (type, message) => {
    setNotify({ open: true, type, message });
  };

  useEffect(() => {
    const node = listContainerRef.current;
    if (!node) return;

    const updateHeight = () => {
      const height = Math.max(120, Math.floor(node.clientHeight));
      const nextScrollbarWidth = Math.max(
        0,
        node.offsetWidth - node.clientWidth
      );
      setListHeight(height);
      setScrollbarWidth(nextScrollbarWidth);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, []);

  const fetchParticipants = useCallback(async (code, showLoader = false) => {
    if (!code) return;
    if (showLoader) {
      setLoading(true);
    }

    try {
      const response = await application.post(
        `conferenceParticipant/confCallDetail/${code}`
      );
      const responseList = response?.data?.data;
      console.log("//////////////////////",responseList)
      const apiMessage = response?.data?.message;
      const apiMessageDetail = response?.data?.messageDetail;
      const nextParticipants = Array.isArray(responseList) ? responseList : [];

      setParticipants((previousParticipants) =>
        isSameParticipantList(previousParticipants, nextParticipants)
          ? previousParticipants
          : nextParticipants
      );

      if (!Array.isArray(responseList)) {
        setError(
          apiMessageDetail ||
            apiMessage ||
            "Participant details are temporarily unavailable."
        );
        return;
      }

      if (responseList.length === 0) {
        setError(
          apiMessageDetail ||
            apiMessage ||
            "Participants are not in the conference right now."
        );
      } else {
        setError("");
      }
    } catch (apiError) {
      setError(
        "We are unable to load participants right now. Please try again shortly."
      );
      loggerFactory.debug("Failed to load conference participants", apiError);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event?.data?.eventType === "conferenceRoomData") {
        const code = getAccessCodeFromEvent(event?.data)?.toString()?.trim();
        if (code) {
          setAccessCode(code);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    if (!accessCode) return;
    fetchParticipants(accessCode, true);
    const interval = setInterval(() => {
      fetchParticipants(accessCode);
    }, 2000);
    return () => clearInterval(interval);
  }, [accessCode, fetchParticipants]);

  const handleMuteToggle = async (participant) => {
    const isUnmuted = participant?.canSpeak !== "false";
    const operation = isUnmuted ? "mute" : "unmute";
    try {
      const response = await application.post(
        `conferenceParticipant/${operation}/${accessCode}/${participant.id}`
      );
      showNotification(
        "success",
        response?.data?.message || "Status updated successfully"
      );
      fetchParticipants(accessCode);
    } catch (apiError) {
      loggerFactory.debug(`Unable to ${operation} participant`, apiError);
      showNotification("error", `Unable to ${operation} participant`);
    }
  };

  const handleKick = async (participant) => {
    try {
      const response = await application.post(
        `conferenceParticipant/${accessCode}/kick/${participant.id}/${participant.contact}`
      );
      showNotification(
        "success",
        response?.data?.messageDetail || "Participant removed"
      );
      fetchParticipants(accessCode);
    } catch (apiError) {
      loggerFactory.debug("Unable to kick participant", apiError);
      showNotification("error", "Unable to remove participant");
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        backgroundColor: "#A6A5A4",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          p: 0,
          height: 28,
          minHeight: 28,
          textAlign: "center",
          backgroundColor: "#ffffff",
          color: "common.white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <GroupsIcon sx={{ fontSize: 18, color: "common.black" }} />
        <Typography variant="body2" sx={{ color: "common.black" }}>
          Active Conference - {conferenceSuffix}
        </Typography>
      </Box>

      {/* {!accessCode && (
        <Stack alignItems="center" justifyContent="center" py={8} spacing={1}>
          <MeetingRoomIcon sx={{ fontSize: 80 }} />
          <Typography
            variant="h6"
            sx={{ color: "text.primary", fontWeight: 700 }}
          >
            Waiting for conference details
          </Typography>
          <Typography variant="h6" color="text.secondary">
            The conference will appear here as soon as event data is received.
          </Typography>
        </Stack>
      )} */}

      {accessCode && loading && (
        <Stack alignItems="center" justifyContent="center" py={6} spacing={2}>
          <CircularProgress />
          <Typography variant="body1">Loading participants...</Typography>
        </Stack>
      )}

      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid #A6A5A4",
          backgroundColor: "#e5e4e2",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: columnTemplate,
            px: 0,
            py: 0,
            pr: `${scrollbarWidth}px`,
            backgroundColor: "#ffffff",
            borderBottom: "1px solid rgba(0, 0, 0, 0.35)",
            minHeight: 25,
            "& .list-cell": {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 0.5,
              height: "100%",
            },
            "& .list-cell:not(:last-of-type)": {
              borderRight: "1px solid rgba(0, 0, 0, 0.35)",
            },
          }}
        >
          <Box className="list-cell">
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "common.black",
                textAlign: "center",
              }}
            >
              Participant
            </Typography>
          </Box>
          <Box className="list-cell">
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "common.black",
                textAlign: "center",
              }}
            >
              Call Type
            </Typography>
          </Box>
          {/* <Box className="list-cell">
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "common.black",
                  textAlign: "center",
                }}
              >
                Mic
              </Typography>
            </Box> */}
          {/* <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: "common.black", textAlign: "center" }}
            >
              Hand Raise
            </Typography> */}
          <Box className="list-cell">
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "common.black",
                textAlign: "center",
              }}
            >
              Speaker
            </Typography>
          </Box>
          <Box className="list-cell">
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                textAlign: "center",
                color: "common.black",
              }}
            >
              Action
            </Typography>
          </Box>
        </Box>

        <Box ref={listContainerRef} sx={{ flex: 1, minHeight: 0 }}>
          {participants.length !== 0 && (
            <FixedSizeList
              height={listHeight}
              itemCount={participants.length}
              itemSize={rowHeight}
              width="100%"
              itemData={{
                participants,
                handleMuteToggle,
                handleKick,
                columnTemplate,
              }}
            >
              {({ index, style, data }) => {
                const participant = data.participants[index];
                const isUnmuted = participant?.canSpeak !== "false";
                const isVideo = participant?.hasVideo === "true";
                const isTalking =
                  participant?.talking === true ||
                  participant?.talking === 1 ||
                  participant?.talking === "1" ||
                  participant?.talking === "true";

                return (
                  <MenuItem
                    style={style}
                    disableRipple
                    sx={{
                      width: "100%",
                      minHeight: rowHeight,
                      p: 0,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.35)",
                      backgroundColor: "#e5e4e2",
                      outline: "none",
                      WebkitTapHighlightColor: "transparent",
                      "&:hover": {
                        backgroundColor: "#e5e4e2",
                      },
                      "&:active": {
                        backgroundColor: "#e5e4e2",
                      },
                      "&:focus": {
                        backgroundColor: "#e5e4e2",
                        outline: "none",
                      },
                      "&:focus-visible": {
                        backgroundColor: "#e5e4e2",
                        outline: "none",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#e5e4e2",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: "#e5e4e2",
                      },
                      "&.Mui-focusVisible": {
                        backgroundColor: "#e5e4e2",
                      },
                      "&.Mui-focusVisible:hover": {
                        backgroundColor: "#e5e4e2",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "grid",
                        gridTemplateColumns: data.columnTemplate,
                        "& .list-cell": {
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 0.5,
                          height: "100%",
                        },
                        "& .list-cell:not(:last-of-type)": {
                          borderRight: "1px solid rgba(0, 0, 0, 0.35)",
                        },
                      }}
                    >
                      <Box className="list-cell">
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "common.black",
                            textAlign: "center",
                          }}
                        >
                          {participant?.contact || "-"}
                        </Typography>
                      </Box>
                      <Box className="list-cell">
                        <Chip
                          icon={isVideo ? <VideocamIcon /> : <CallIcon />}
                          label={isVideo ? "Video Call" : "Audio Call"}
                          color={"warning"}
                          size="small"
                          sx={{
                            color: "common.black",
                            height: 20,
                            "& .MuiChip-icon": {
                              color: "common.black",
                              fontSize: 14,
                            },
                            "& .MuiChip-label": {
                              px: 0.75,
                              fontSize: "0.65rem",
                            },
                          }}
                        />
                      </Box>
                      {/* <Box className="list-cell">
                          <Chip
                            label={isUnmuted ? "Unmute" : "Mute"}
                            color={isUnmuted ? "success" : "error"}
                            size="small"
                            sx={{
                              color: "common.black",
                              height: 20,
                              "& .MuiChip-icon": {
                                color: "common.black",
                                fontSize: 14,
                              },
                              "& .MuiChip-label": {
                                px: 0.75,
                                fontSize: "0.65rem",
                              },
                            }}
                          />
                        </Box> */}
                      {/* <Box>
                          {isHandRaised && (
                            <Chip
                              icon={<PanToolIcon />}
                              label="Hand Raised"
                              color="warning"
                              size="small"
                            />
                          )}
                        </Box> */}
                      <Box className="list-cell">
                        {isTalking ? (
                          <Tooltip title="Speaking">
                            <VolumeUpIcon fontSize="medium" color="success" />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </Box>
                      <Box className="list-cell">
                        <Stack
                          direction="row"
                          spacing={2.5}
                          justifyContent="center"
                        >
                          <Tooltip title={isUnmuted ? "Mute" : "Unmute"}>
                            <IconButton
                              size="small"
                              color={isUnmuted ? "success" : "error"}
                              sx={{ p: 0.25 }}
                              onClick={() => data.handleMuteToggle(participant)}
                            >
                              {isUnmuted ? (
                                <MicIcon sx={{ fontSize: 16 }} />
                              ) : (
                                <MicOffIcon sx={{ fontSize: 16 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Kick Out">
                            <IconButton
                              size="small"
                              color="error"
                              sx={{ p: 0.25 }}
                              onClick={() => data.handleKick(participant)}
                            >
                              <PersonRemoveIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    </Box>
                  </MenuItem>
                );
              }}
            </FixedSizeList>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={notify.open}
        autoHideDuration={3000}
        onClose={() => setNotify((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={notify.type}
          onClose={() => setNotify((prev) => ({ ...prev, open: false }))}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notify.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
